import qs from 'qs';

import * as EC from '@luxuryescapes/contract-svc-experience';
import { API } from '@luxuryescapes/lib-types';

import { uploadImage } from '~/services/ImageService';

import { json_headers, request } from './common';

const STATIC_PARAMS = `currency=AUD`;
const BASE_PATH = `${window.configs.API_HOST}/api/experiences`;

export type ExperienceItem = API.Order.ExperienceItem;
export type ExperienceItems = Array<API.Order.ExperienceItem>;

export type OfferBrandSchedule = {
  brandId: string;
  startDate?: string | null;
  endDate?: string | null;
  expiryDate?: string | null;
  tacticalEnabled?: boolean;
  tacticalPromoStart?: string | null;
  tacticalPromoEnd?: string | null;
};

export type CategoryItem = EC.Experiences.Category;

// TODO: remove local type after contract-svc-experience is bumped to include memberDiscountEnabled/memberDiscountOverrides
export type MemberDiscountOverrides = {
  marginOverride?: number;
  floorOverride?: number;
  maxDiscountCapPct?: number;
  forceEnable?: boolean;
  forceDisable?: boolean;
};

export type ExperienceOffer = EC.Experiences.Offer & {
  memberDiscountEnabled?: boolean;
  memberDiscountOverrides?: MemberDiscountOverrides;
};

export type ExperienceUrgencyTag = {
  id: string;
  text: string;
  bgColor: string;
  textColor: string;
};
export type ExperienceOffers = Array<ExperienceOffer>;
export type ExperienceOfferImage = EC.Experiences.Image;
export type CurationStatus = EC.Experiences.CurationStatus | string;
export type DateAvailability = EC.Experiences.DateAvailability;
export type TimeGroups = EC.Experiences.TimeGroups;
export type DealTicket = EC.Experiences.DealTicket;
export type ExperienceActionTypes = 'LOAD' | 'FETCH' | 'ERROR' | 'RESET';
export type BookingOptions = EC.Experiences.BookingOptions;
export type Brand = EC.Experiences.Brand;
export type BookingDetails = EC.Experiences.BookingDetails;

// TODO: remove after contract-svc-experience is bumped to include umbrellaChildren
export type UmbrellaChildSummary = {
  id: string;
  title: string;
  providerId: string;
  heroImageUrl?: string;
  curationStatus: string;
};

export interface RangeFilter {
  min: number | null;
  max: number | null;
}

export type GoLiveDateInWeeks = 1 | 2 | 4;

export interface Filters {
  limit?: number | null;
  offset?: number | null;
  distance?: string | null;
  price?: RangeFilter | null;
  rating?: RangeFilter | null;
  categories?: Array<string> | null;
  status?: Array<CurationStatus> | null;
  coordinates?: google.maps.LatLngLiteral | null;
  idIn?: string | null;
  title?: string | null;
  vendor?: string | null;
  providerIn?: Array<string> | null;
  collectionIn?: string | null;
  goLiveDateInWeeks?: GoLiveDateInWeeks | null;
}

export type Hero = {
  experiencesOffersIds: Array<string>;
  region: string;
};

export const initialFiltersValues: Filters = {
  limit: null,
  offset: null,
  status: null,
  distance: null,
  categories: null,
  coordinates: null,
  price: { min: null, max: null },
  rating: { min: null, max: null },
  idIn: null,
  title: null,
  vendor: null,
  providerIn: null,
  collectionIn: null,
  goLiveDateInWeeks: null,
};

type GetOffersParams = {
  limit?: number;
  brand: string;
  offset?: number;
  filters?: Filters;
  curationData?: boolean;
  minified?: boolean;
  sort?: string;
  sortOrientation?: string;
  getController?: (controller: { cancel: () => void }) => void;
  bundledPackageId?: string;
  disableCache?: boolean;
};

type OffersResponse = EC.Experiences.Response<Array<EC.Experiences.Offer>>;

const getExperienceOffers = (params: GetOffersParams): Promise<OffersResponse> => {
  const {
    filters,
    limit,
    offset,
    brand,
    curationData,
    minified,
    getController,
    sort,
    sortOrientation,
    bundledPackageId,
    disableCache = true,
  } = params;
  const queryParams: EC.Experiences.FilterQuery & { goLiveDateInWeeks?: GoLiveDateInWeeks } = {
    limit,
    offset,
    brand,
    curationData,
    minified,
    showUnlisted: true,
    disableCache,
    sort,
    sortOrientation,
    bundledPackageId,
  };

  if (!filters?.goLiveDateInWeeks) {
    queryParams.showFutureOffers = true;
  }

  if (filters?.distance) queryParams.distance = filters.distance;

  if (filters?.categories?.length > 0) queryParams.categoryIn = filters.categories?.join();

  if (filters?.status?.length > 0) queryParams.curationStatus = filters.status.join();
  else queryParams.curationStatus = 'APPROVED,REJECTED,NOT_CURATED';

  if (filters?.coordinates) queryParams.coordinates = `${filters.coordinates.lat},${filters.coordinates.lng}`;

  if (filters?.rating?.min || filters?.rating?.max)
    queryParams.ratingRange = `${filters.rating.min || 0},${filters.rating.max || 5}`;

  if (filters?.price?.min || filters?.price?.max)
    queryParams.basePriceRange = `${filters.price.min || 0},${filters.price.max || 99999}`;

  if (filters?.idIn) queryParams.idIn = filters.idIn;

  if (filters?.title) queryParams.title = filters.title;

  if (filters?.vendor) queryParams.vendor = filters.vendor;

  if (filters?.providerIn?.length > 0) {
    queryParams.providerIn = filters.providerIn.join();
  }

  if (filters?.collectionIn) {
    queryParams.collectionIn = filters.collectionIn;
  }

  if (filters?.goLiveDateInWeeks) {
    queryParams.goLiveDateInWeeks = filters.goLiveDateInWeeks;
  }

  const requestController = new AbortController();
  const { signal } = requestController;
  if (getController) getController({ cancel: () => requestController.abort() });

  const FILTER_PARAMS = qs.stringify(queryParams);
  const uri = `${BASE_PATH}/offers?${STATIC_PARAMS}&${FILTER_PARAMS}`;

  return request(uri, { signal, method: 'GET' });
};

type OfferByIdParams = { id: string; brand: string; curationData?: boolean; signal?: AbortSignal };
type OfferByIdResponse = EC.Experiences.Response<EC.Experiences.Offer>;
type HeroesResponse = EC.Experiences.Response<Hero>;
type BookingDetailsResponse = EC.Experiences.Response<Array<EC.Experiences.BookingDetails>>;

const getExperienceOfferById = (params: OfferByIdParams): Promise<OfferByIdResponse> => {
  const { id, brand, curationData = false, signal } = params;
  const uri = `${BASE_PATH}/offers/${id}?${STATIC_PARAMS}&brand=${brand}&curationData=${curationData}&disableCache=true`;

  return request(uri, { method: 'GET', signal });
};

type CategoriesResponse = EC.Experiences.Response<Array<EC.Experiences.Category>>;

const getCategories = (brand: string): Promise<CategoriesResponse> => {
  const uri = `${BASE_PATH}/categories?currency=AUD&brand=${brand}`;
  return request(uri, { method: 'GET' });
};
const getHeroesByRegion = (region: string, brand: string, placeId?: string): Promise<HeroesResponse> => {
  const uri = `${BASE_PATH}/region-heroes/${region}?brand=${brand}&disableCache=true&placeId=${placeId}`;
  return request(uri, { method: 'GET' });
};

export type UpdateRegionHeroesPayload =
  EC.operations['/api/experiences/region-heroes/{placeId}/patch']['requestBody']['content']['application/json'];
const updateRegionHeroes = (id: string, payload: UpdateRegionHeroesPayload): Promise<HeroesResponse> => {
  const uri = `${BASE_PATH}/region-heroes/${id}`;
  return request(uri, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export type UploadedImage = {
  id: string;
  url: string;
};

export type TicketsPayload = DealTicket & {
  image?: File;
};

export interface CurationParams {
  curationStatus?: CurationStatus;
  isFlash?: boolean;
  title?: string;
  location?: {
    latitude: number | null;
    longitude: number | null;
    description: string | null;
  };
  categories?: Array<EC.Experiences.Category>;
  heroImageId?: string;
  campaigns?: Array<string>;
  showPostPurchase?: boolean;
  searchEngineMarketingEnabled?: boolean;
  unlisted?: EC.Experiences.Offer['unlisted'] | null;
  dealDescription?: string;
  shortDescription?: string;
  included?: string;
  thingsToKnowNew?: EC.Experiences.ThingsToKnow;
  tickets?: Array<EC.Experiences.DealTicket>;
  images?: Array<EC.Experiences.Image>;
  hideTimeSlots?: boolean;
  isNonRefundable?: boolean;
  video?: string;
  excludedRegions?: EC.Experiences.ExcludedRegion;
  bookingOptions?: EC.Experiences.BookingOptions;
  vendorBookingCutoff?: number;
  features?: EC.Experiences.OfferFeatures;
  ticketUnitLabel?: string;
  baseSalesPrice?: {
    amount: number;
    currencyCode: string;
  };
  allowBuyNowBookLater?: boolean;
  allowGiftCard?: boolean;
  idSalesforceExternal?: string | null;
  brands?: Array<string>;
  cancellationInfo?: EC.Experiences.CancellationInfo;
  showInJourneys?: boolean;
  bookingFields?: Array<EC.Experiences.BookingField>;
  margin?: number;
  commissionMargin?: number;
  bundleMinMarkupPct?: number;
  ticketed?: boolean;
  targetAudiences?: EC.Experiences.Offer['targetAudiences'];
  disableReviews?: boolean;
  curationComment?: string;
  memberDiscountEnabled?: boolean;
  memberDiscountOverrides?: MemberDiscountOverrides;
  brandSchedules?: Array<{
    brandId: string;
    tacticalPromoStart?: string | null;
    tacticalPromoEnd?: string | null;
    tacticalEnabled?: boolean;
  }>;
}

type CurationResponse = EC.Experiences.Response<EC.Experiences.Offer>;

export enum TransferType {
  AIRPORT_TO_HOTEL = 'AIRPORT-TO-HOTEL',
  HOTEL_TO_AIRPORT = 'HOTEL-TO-AIRPORT',
}

export enum ChildSeatType {
  INFANT_SEAT = 'INFANT-SEAT',
  BOOSTER_SEAT = 'BOOSTER-SEAT',
}

export interface CurateExperienceParams {
  curationStatus: CurationStatus;
}

const uploadImageFile = async (image: File): Promise<{ id: string; url: string }> => {
  const response = await uploadImage(image);
  return {
    id: response.public_id,
    url: `${window.configs.IMAGE_HOST}/${response.public_id}.${image.type.split('/').pop()}`,
  };
};

const mapOfferTickets = async (
  tickets: Array<TicketsPayload> | undefined,
): Promise<Array<EC.Experiences.DealTicket>> | undefined => {
  if (!tickets) return;

  const mappedTickets: Array<EC.Experiences.DealTicket> = await Promise.all(
    tickets.map(async (ticket) => {
      let imageUrl = ticket.imageUrl;

      if (ticket.image) {
        const { url } = await uploadImageFile(ticket.image);
        imageUrl = url;
      }

      delete ticket.image;

      return { ...ticket, imageUrl };
    }),
  );

  return mappedTickets;
};

const uploadOfferImage = async (images: Array<File>): Promise<Array<UploadedImage>> => {
  return Promise.all(images.map((image) => uploadImageFile(image)));
};

const curationExperience = async (
  experienceId: string,
  brand: string,
  payload: CurationParams,
): Promise<CurationResponse> => {
  const uri = `${BASE_PATH}/offers/${experienceId}?${STATIC_PARAMS}&brand=${brand}`;

  return request(uri, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

type AvailableDatesResponse = EC.Experiences.Response<Array<EC.Experiences.DateAvailability>>;
type AvailableTimesResponse = EC.Experiences.Response<Array<EC.Experiences.TimeGroups>>;
type OriginalOfferResponse = EC.Experiences.Response<ExperienceOffer>;

type CurationFeatureManagerResponse = EC.Experiences.Response<EC.Experiences.CurationFeatureManager>;

type BrandsResponse = EC.Experiences.Response<Array<EC.Experiences.Brand>>;

type ExperienceOfferTemplatesResponse = EC.Experiences.Response<Array<EC.Experiences.OfferTemplate>>;

export type CurationFeatureManager = EC.Experiences.CurationFeatureManager;

const getAvailableDates = (experienceId: string, brand: string): Promise<AvailableDatesResponse> => {
  const uri = `${BASE_PATH}/offers/${experienceId}/availability?brand=${brand}`;
  return request(uri, { method: 'GET' });
};

const getAvailableTimes = (
  experienceId: string,
  brand: string,
  payload: { day: string; bundledPackageId?: string; ticketModesMap?: Record<string, string> },
): Promise<AvailableTimesResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.set('brand', brand);
  queryParams.set('curationData', 'true');

  if (payload.bundledPackageId) {
    queryParams.set('bundledPackageId', payload.bundledPackageId);
  }

  if (payload.ticketModesMap) {
    Object.entries(payload.ticketModesMap).forEach(([k, v]) => {
      queryParams.append(`ticketModesMap[${k}]`, v);
    });
  }

  const uri = `${BASE_PATH}/offers/${experienceId}/availability/${payload.day}?${queryParams.toString()}`;

  return request(uri, { method: 'GET' });
};

/**
 * Returns the original experience from provider
 */
const getOriginalOffer = (experienceId: string, brand: string): Promise<OriginalOfferResponse> => {
  const uri = `${BASE_PATH}/offers/${experienceId}/original?brand=${brand}`;
  return request(uri, { method: 'GET' });
};

const getHandpickedExperiencesForOffer = (offerId: string) => {
  return request(`${BASE_PATH}/offer-handpicked-experiences?offerId=${offerId}`, { method: 'GET' });
};

const postHandpickedExperiencesForOffer = (payload) => {
  const jsonData = JSON.stringify(payload);
  return request(`${BASE_PATH}/offer-handpicked-experiences`, {
    method: 'POST',
    headers: json_headers,
    body: jsonData,
  });
};

const getHandpickedExperiencesForPackage = (packageId: string) => {
  return request(`${BASE_PATH}/package-handpicked-experiences?packageId=${packageId}`, { method: 'GET' });
};

const postHandpickedExperiencesForPackage = (payload) => {
  const jsonData = JSON.stringify(payload);
  return request(`${BASE_PATH}/package-handpicked-experiences`, {
    method: 'POST',
    headers: json_headers,
    body: jsonData,
  });
};

const getBookingDetails = (experienceItemId: string): Promise<BookingDetailsResponse> => {
  return request(`${BASE_PATH}/booking-details/${experienceItemId}`, {
    method: 'GET',
  });
};

const getCurationFeatureManager = (experienceId: string): Promise<CurationFeatureManagerResponse> => {
  const uri = `${BASE_PATH}/offers/${experienceId}/curation-feature-manager`;
  return request(uri, { method: 'GET' });
};

const getBrands = (): Promise<BrandsResponse> => {
  const uri = `${BASE_PATH}/brands`;
  return request(uri, { method: 'GET' });
};

export async function getSvcExperienceSyncData(packageId) {
  return request(`${BASE_PATH}/offers?bundledPackageId=${packageId}&showUnlisted=true`, {
    method: 'GET',
  });
}

const getExperienceOfferTemplates = (): Promise<ExperienceOfferTemplatesResponse> => {
  const uri = `${BASE_PATH}/templates/all`;
  return request(uri, { method: 'GET' });
};

const uploadVoucher = (formData: FormData): Promise<{ voucherUrl: string }> => {
  const uri = `${BASE_PATH}/items/voucher/upload`;
  return request(uri, { method: 'POST', body: formData });
};

const uploadAllVouchers = (formData: FormData): Promise<{ voucherUrl: string }> => {
  const uri = `${BASE_PATH}/items/voucher/upload-all`;
  return request(uri, { method: 'POST', body: formData });
};

const downloadVoucher = async ({
  orderId,
  experienceItemIds,
  experienceOfferId,
  downloadAllOfferItemsVoucher,
}: {
  orderId: string;
  experienceItemIds: Array<string>;
  experienceOfferId?: string;
  downloadAllOfferItemsVoucher?: boolean;
}): Promise<Blob> => {
  const queryParams = qs.stringify({
    experienceItemIds,
    experienceOfferId,
    downloadAllOfferItemsVoucher: downloadAllOfferItemsVoucher ? 'true' : 'false',
  });

  const uri = `${BASE_PATH}/items/order/${orderId}/vouchers/download?${queryParams}`;
  return request(uri, { download: true });
};

const deleteVoucher = ({
  voucherUrl,
  experienceItemIds,
  brand,
}: {
  voucherUrl: string;
  experienceItemIds: Array<string>;
  brand: string;
}): Promise<void> => {
  const uri = `${BASE_PATH}/items/voucher`;
  return request(uri, {
    method: 'DELETE',
    headers: json_headers,
    body: JSON.stringify({
      voucherUrl,
      experienceItemIds,
      brand,
    }),
  });
};

type SyncOfferBody = EC.operations['/api/experiences/offers/sync/post']['requestBody']['content']['application/json'];

/** Add tasks to perform the sync one job from the provider API */
const syncOneOffer = ({
  providerKey,
  providerIds,
}: {
  providerKey: SyncOfferBody['providerKey'];
  providerIds: Array<string>;
}) => {
  const uri = `${BASE_PATH}/offers/sync`;
  const body: SyncOfferBody = {
    providerIds,
    providerKey,
  };
  return request(uri, { method: 'POST', headers: json_headers, body: JSON.stringify(body) });
};

export interface MetricsWindow {
  sessionsWithViews: number;
  clicks: number;
  orders: number;
  grossSalesAud: number;
  marginAud: number;
  daysWithViews: number;
  updatedAt: string;
}

export interface HistoricalMetrics extends MetricsWindow {
  totalDays: number;
  dailyAvgViews: number;
  dailyAvgOrders: number;
}

export interface CollectionMetrics {
  historical: HistoricalMetrics;
  last7d: MetricsWindow;
}

export type Collection = {
  id: string;
  theme: string;
  definition?: string;
  isActive: boolean;
  strongIndicators?: string;
  weakIndicators?: string;
  metrics7d?: CollectionMetrics | null;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
};

// CarouselMetric reuses CollectionMetrics for metrics7d intentionally:
// both collections and carousels share the same BQ query structure (last7d + historical windows).
export type CarouselMetric = {
  carouselName: string;
  metrics7d?: CollectionMetrics | null;
  updatedAt?: string | null;
};

const getCollections = async (onlyActive = false): Promise<Array<Collection>> => {
  const uri = `${BASE_PATH}/collections?onlyActive=${onlyActive}`;
  const response = await request(uri, { method: 'GET' });
  return response.result || [];
};

const updateCollection = async (
  collectionId: string,
  updates: Partial<Omit<Collection, 'id'>>,
): Promise<Collection> => {
  const uri = `${BASE_PATH}/collections/${collectionId}`;
  const response = await request(uri, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(updates),
  });
  return response.result;
};

export type OnDemandCarouselMetric = {
  carouselName: string;
  metrics: MetricsWindow;
};

export type GeoFilter = {
  country?: string;
  region?: string;
  city?: string;
};

export type ContentTagPerformanceSort = 'bookings' | 'revenue' | 'margin' | 'marginPct' | 'name';

export type ContentTagPerformanceParams = {
  from?: string;
  to?: string;
  countryCode?: string;
  lePlaceId?: string;
  search?: string;
  sort?: ContentTagPerformanceSort;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

export type ContentTagPerformanceRow = {
  contentTagId: string;
  name: string;
  bookings: number;
  revenue: number;
  margin: number;
  marginPct: number;
};

export type ContentTagPerformanceResponse = {
  result: Array<ContentTagPerformanceRow>;
  total: number;
  updatedAt: string;
};

async function getCarouselMetrics(): Promise<Array<CarouselMetric>>;
async function getCarouselMetrics(
  startDate: string,
  endDate: string,
  geo?: GeoFilter,
): Promise<Array<OnDemandCarouselMetric>>;
async function getCarouselMetrics(startDate?: string, endDate?: string, geo?: GeoFilter) {
  const queryParams = new URLSearchParams();
  if (startDate && endDate) {
    queryParams.set('startDate', startDate);
    queryParams.set('endDate', endDate);
  }
  if (geo?.country) queryParams.set('country', geo.country);
  if (geo?.region) queryParams.set('region', geo.region);
  if (geo?.city) queryParams.set('city', geo.city);
  const qs = queryParams.toString();
  const uri = `${BASE_PATH}/carousel-metrics${qs ? `?${qs}` : ''}`;
  const response = await request(uri, { method: 'GET' });
  return response.result || [];
}

async function getGeoValues(country: string, field: 'region' | 'city', region?: string): Promise<Array<string>> {
  const queryParams = new URLSearchParams({ country, field });
  if (region) queryParams.set('region', region);
  const uri = `${BASE_PATH}/carousel-metrics/geo-values?${queryParams.toString()}`;
  const response = await request(uri, { method: 'GET' });
  return response.result || [];
}

async function getContentTagPerformance(params: ContentTagPerformanceParams): Promise<ContentTagPerformanceResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const uri = `${BASE_PATH}/content-tags/performance${query ? `?${query}` : ''}`;
  const response = await request(uri, { method: 'GET' });

  return {
    result: response.result || [],
    total: response.total || 0,
    updatedAt: response.updatedAt || '',
  };
}

export type SyncLog = {
  id: string;
  offerId: string;
  provider: string;
  log: Record<string, unknown>;
  createdAt: string;
};

async function getSyncLogs(offerId: string, signal?: AbortSignal): Promise<Array<SyncLog>> {
  const uri = `${BASE_PATH}/sync-logs?offerId=${encodeURIComponent(offerId)}`;
  const response = await request(uri, { method: 'GET', signal });
  return response.result || [];
}

const deleteCollectionOfferLink = async ({
  collectionId,
  offerId,
}: {
  collectionId: string;
  offerId: string;
}): Promise<void> => {
  const uri = `${BASE_PATH}/collections/${collectionId}/offer/${offerId}`;
  return request(uri, { method: 'DELETE', headers: json_headers });
};

const getExperienceUrgencyTags = async (): Promise<Array<ExperienceUrgencyTag>> => {
  const response = await request(`${BASE_PATH}/urgency-tags`, { method: 'GET' });
  return response.result || [];
};

const attachExperienceOfferUrgencyTag = async (
  offerId: string,
  urgencyTagId: string,
): Promise<ExperienceUrgencyTag> => {
  const uri = `${BASE_PATH}/offers/${offerId}/urgency-tags`;
  const response = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ urgencyTagId }),
  });
  return response.result;
};

const detachExperienceOfferUrgencyTag = async (offerId: string, urgencyTagId: string): Promise<void> => {
  const uri = `${BASE_PATH}/offers/${offerId}/urgency-tags/${urgencyTagId}`;
  await request(uri, { method: 'DELETE', headers: json_headers });
};

export type BulkCategoryRow = {
  offerId: string;
  categories: string[];
  action: 'add' | 'remove';
};

export type BulkCategoryResult = {
  updated: string[];
  failed: Array<{ offerId: string; reason: string }>;
};

export type UncategorisedOffer = {
  id: string;
  title: string;
  brands: string[];
};

async function bulkUpdateCategories(rows: BulkCategoryRow[]): Promise<BulkCategoryResult> {
  const uri = `${BASE_PATH}/offers/bulk-categories`;
  const response = await request(uri, { method: 'PATCH', headers: json_headers, body: JSON.stringify(rows) });
  return response.result;
}

async function getUncategorisedOffers(): Promise<Array<UncategorisedOffer>> {
  const uri = `${BASE_PATH}/offers/uncategorised`;
  const response = await request(uri, { method: 'GET' });
  return response.result || [];
}

export {
  BASE_PATH,
  getCategories,
  curationExperience,
  getExperienceOffers,
  getExperienceOfferById,
  getHeroesByRegion,
  updateRegionHeroes,
  getAvailableTimes,
  getAvailableDates,
  getHandpickedExperiencesForOffer,
  postHandpickedExperiencesForOffer,
  getHandpickedExperiencesForPackage,
  postHandpickedExperiencesForPackage,
  uploadOfferImage,
  getOriginalOffer,
  getBookingDetails,
  getCurationFeatureManager,
  mapOfferTickets,
  getBrands,
  getExperienceOfferTemplates,
  uploadVoucher,
  uploadAllVouchers,
  downloadVoucher,
  deleteVoucher,
  syncOneOffer,
  getCollections,
  updateCollection,
  deleteCollectionOfferLink,
  getExperienceUrgencyTags,
  attachExperienceOfferUrgencyTag,
  detachExperienceOfferUrgencyTag,
  getCarouselMetrics,
  getGeoValues,
  getContentTagPerformance,
  getSyncLogs,
  bulkUpdateCategories,
  getUncategorisedOffers,
};
